import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  speechInspectionAPI,
  updateSpeechInspectionAPI,
} from "../../../../services/speech.services";
import {
  Button,
  Col,
  Form,
  message,
  Modal,
  Row,
  Tabs,
} from "antd";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import SpeechSwallowGeneralExamination from "../../InspectionForms/Examination/GeneralExaminationForm/SpeechSwallowGeneralExamination";
import SpeechSwallowScales from "../../InspectionForms/Scales/SpeechSwallowScales";
import SpeechSwallowInitialFunctionalDiagnosis from "../../InspectionForms/FunctionalDiagnosis/SpeechSwallowInitialFunctionalDiagnosis";
import { SpeechInitialContext } from "./SpeechSwallowInitialAssessmentProvider";
import useIsMobile from "../../../../hooks/useIsMobile";
import "../../ipforms.css";
import "../../Doctor/InitialInspection/InitialInspectionHeader.css";

const SpeechSwallowInitialInspectionForm = ({
  setVisible,
  refreshPage,
  initialRefresh,
  editId,
  setConfirmModal,
  patientData,
  activeTab,
  setActiveTab,
}) => {
  const isMobile = useIsMobile();
  const formBodyRef = useRef(null);
  const location = useLocation();
  const params = Object.fromEntries(new URLSearchParams(location?.search));
  const [generalForm] = Form.useForm();
  const [functionalForm] = Form.useForm();
  const [scalesForm] = Form.useForm();
  const { formData } = useContext(SpeechInitialContext);
  const [isDirty, setIsDirty] = useState(false);
  const [buttonPressed, setButtonPressed] = useState(false);

  const tabItems = useMemo(
    () => [
      {
        key: "1",
        label: isMobile ? "General Exam" : "General Examination",
      },
      { key: "2", label: "Scales" },
      {
        key: "3",
        label: isMobile ? "Diagnosis" : "Functional Diagnosis",
      },
    ],
    [isMobile]
  );

  useEffect(() => {
    if (formBodyRef.current) {
      formBodyRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  const handleSubmit = async (values, flag) => {
    try {
      if (editId) {
        await updateSpeechInspectionAPI(editId, values);
        initialRefresh();
      } else {
        await speechInspectionAPI(values);
        refreshPage();
        initialRefresh();
      }

      message.success(`${flag} submitted successfully`);
      if (buttonPressed) {
        if (activeTab === "3") {
          setVisible(false);
        } else {
          const nextTab = (parseInt(activeTab) + 1).toString();
          setActiveTab(nextTab);
        }
        setButtonPressed(false);
      }
      setIsDirty(false);
    } catch (error) {
      console.log(error, "Error while submitting");
      message.error(error?.response?.data?.message || "Something went wrong");
    }
  };

  const onGeneralSubmit = (values) => {
    handleSubmit(
      {
        general_examination: values,
        admission_id: params?.patient,
      },
      "General Examination"
    );
  };

  const onFunctionalSubmit = (values) => {
    handleSubmit(
      {
        functional_diagnosis: values,
        admission_id: params?.patient,
      },
      "Functional Diagnosis"
    );
  };

  const onScalesSubmit = (values) => {
    handleSubmit(
      {
        scales: values,
        admission_id: params?.patient,
      },
      "Scales"
    );
  };

  const getFormKey = () => {
    switch (activeTab) {
      case "1":
        return generalForm.submit();
      case "2":
        return scalesForm.submit();
      case "3":
        return functionalForm.submit();
      default:
        return "";
    }
  };

  const handleTabChange = (key) => {
    if (isDirty) {
      Modal.confirm({
        title: "Unsaved changes",
        content:
          "You have unsaved changes. Are you sure you want to save these changes?",
        onOk: () => {
          switch (activeTab) {
            case "1":
              generalForm.validateFields().then((values) => {
                setIsDirty(false);
                onGeneralSubmit(values);
                setActiveTab(key);
              });
              break;
            case "2":
              scalesForm.validateFields().then((values) => {
                setIsDirty(false);
                onScalesSubmit(values);
                setActiveTab(key);
              });
              break;
            case "3":
              functionalForm.validateFields().then((values) => {
                setIsDirty(false);
                onFunctionalSubmit(values);
                setActiveTab(key);
              });
              break;

            default:
              setActiveTab(key);
          }
        },
        onCancel: () => {
          setActiveTab(key);
          setIsDirty(false);
        },
      });
    } else {
      setActiveTab(key);
    }
  };

  const onFormValuesChange = () => {
    setIsDirty(true);
  };

  return (
    <div className="initial-inspection-form-wrapper">
      <div className="ia-form-tabs">
        <Row>
          <Col
            xs={24}
            sm={24}
            md={{ span: 23, offset: 1 }}
            lg={{ span: 21, offset: 3 }}
            xl={{ span: 19, offset: 5 }}
            xxl={{ span: 18, offset: 6 }}
          >
            <Tabs
              activeKey={activeTab}
              items={tabItems}
              onChange={handleTabChange}
            />
          </Col>
        </Row>
      </div>
      <div className="ia-form-panel">
        <div className="ia-form-body" ref={formBodyRef}>
          {activeTab === "1" && (
            <Form
              id="general_examination-form"
              form={generalForm}
              layout="vertical"
              onFinish={onGeneralSubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData}
              className="ip-forms"
            >
              <SpeechSwallowGeneralExamination
                form={generalForm}
                formData={formData}
              />
            </Form>
          )}
          {activeTab === "2" && (
            <Form
              id="scales-form"
              form={scalesForm}
              layout="vertical"
              onFinish={onScalesSubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData}
              className="ip-forms"
            >
              <SpeechSwallowScales form={scalesForm} formData={formData} />
            </Form>
          )}
          {activeTab === "3" && (
            <Form
              id="functional-diagnosis-form"
              form={functionalForm}
              layout="vertical"
              onFinish={onFunctionalSubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData}
              className="ip-forms"
            >
              <SpeechSwallowInitialFunctionalDiagnosis
                form={functionalForm}
                context={SpeechInitialContext}
              />
            </Form>
          )}
        </div>
        <div className="ia-form-footer">
          <Button
            onClick={() => {
              getFormKey();
              setButtonPressed(true);
              document
                .querySelector(".initial-inspection-form-wrapper")
                ?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
            }}
            type="primary"
            className={btnStyles.mdBtn}
            style={{ fontWeight: 500 }}
          >
            {activeTab === "3" ? "Done" : " Save & Proceed"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SpeechSwallowInitialInspectionForm;
